import { useAppDispatch, useAppSelector } from "../../store";
import { AnnotatedResource } from "../resources/types";
import { WindowSize } from "../settings/types";
import { requestRender } from "./functions";
import { currentThemeSelector } from '../ui/selectors';
import { rendererActions } from './slice';
import { CacheRenderedResourceAction } from './types';


const PROCESSING: string[] = [];

export const useRenderCache = () => {

  const render = useAsyncRender();
  const themeCaches = useAppSelector(state => state.renderCache);
  const globalTheme = useAppSelector(currentThemeSelector);

  return (resource: AnnotatedResource | null): AnnotatedResource | null => {
    if (!resource) {
      return null;
    }

    const requiredThemeID = resource?.annotations?.themeID || globalTheme?.id;
    const themeCache = themeCaches[requiredThemeID as string];

    if (resource && (!themeCache || !themeCache[resource.id])) {
      // We need to return synchronously - but the render result will be cached
      // in the meantime and this hook rerun when the cache changes. The result
      // will be returned then.
      render(resource);
    }
    return themeCache?.[resource.id];
  };

};

export const useAsyncRender = () => {
  const canvasSize: WindowSize = useAppSelector(state => state.settings.display);
  const globalTheme = useAppSelector(currentThemeSelector);
  const themes = useAppSelector(state => state.themes);

  const themeCaches = useAppSelector(state => state.renderCache);

  const dispatch = useAppDispatch();

  return async (resource: AnnotatedResource | null, forceRerender: boolean = false): Promise<CacheRenderedResourceAction | null> => {

    const requiredTheme = resource?.annotations?.themeID ? themes[resource.annotations.themeID as string] : globalTheme;
    if (!requiredTheme) {
      return null;
    }
    const themeCache = themeCaches[requiredTheme!.id];

    if (resource) {
      if (themeCache?.[resource.id] && !forceRerender) {
        return { resource: themeCache[resource.id], themeID: requiredTheme.id };
      }

      if (canvasSize && !PROCESSING.includes(resource.id)) {
        const idx = PROCESSING.push(resource.id);
        const rendered = await requestRender(resource, requiredTheme, canvasSize) as CacheRenderedResourceAction;
        dispatch(rendererActions.cacheRenderedResource(rendered));
        PROCESSING.splice(idx - 1, 1);
        return rendered;
      }
    }


    return null;

  };
};
